import { downloadFile } from '@faceup/utils'
import { ok } from 'neverthrow'
import { Stream } from './atoms/stream'
import { readReportKey } from './report'
import { createErr, mapErr } from './utils/general'

export type FileDescriptor = {
  url: string
  name: string
  mimetype: string
}

export const loadFileWithReportKey = async (
  { url, name, mimetype }: FileDescriptor,
  recipientKey: string
) => {
  const file = (await loadFile({ url, name, mimetype })).value

  const reportKey = await readReportKey(recipientKey)
  if (reportKey.isErr()) {
    return mapErr(reportKey, '[loadFileWithReportKey]: Could not read report key when loading file')
  }

  const decryptedFile = await decryptFileWithReportKey(file, reportKey.value)
  if (decryptedFile.isErr()) {
    return mapErr(decryptedFile, '[loadFileWithReportKey]: Could not decrypt file with report key')
  }

  return ok(decryptedFile.value)
}

export const loadFile = async ({ url, name, mimetype }: FileDescriptor) => {
  const data = await fetch(url)
  const blob = await data.blob()
  const file = new File([blob], name, { type: mimetype })

  return ok(file)
}

export const downloadFileWithoutDecrypting = async (
  file: FileDescriptor,
  download: (blob: Blob, name: string) => void = downloadFile
) => {
  const loadedFile = await loadFile(file)
  if (loadedFile.isErr()) {
    return mapErr(loadedFile, 'Could not download file')
  }

  return ok(download(loadedFile.value, file.name))
}

export const downloadFileWithReportKey = async (
  file: FileDescriptor,
  recipientKey: string,
  // Define this function if standard download using `.click()` on created element is not available (e.g. mobile app)
  download: (blob: Blob, name: string) => void = downloadFile
) => {
  const decryptedFile = await loadFileWithReportKey(file, recipientKey)
  if (decryptedFile.isErr()) {
    return mapErr(
      decryptedFile,
      '[downloadFileWithReportKey]: Could not download file with report key'
    )
  }

  return ok(download(decryptedFile.value, file.name))
}

export const decryptFileWithReportKey = async (file: File, reportKey: Uint8Array) => {
  const buffer = await file.arrayBuffer()
  const decryptedBuffer = await decryptBufferWithReportKey(new Uint8Array(buffer), reportKey)

  if (decryptedBuffer.isErr()) {
    return mapErr(decryptedBuffer, 'Could not decrypt file with report key')
  }

  return ok(new File([decryptedBuffer.value], file.name, { type: file.type }))
}

export const decryptBufferWithReportKey = async (buffer: Uint8Array, reportKey: Uint8Array) => {
  const decryptedChunks = await Stream.decrypt(Stream.toStreamKey(reportKey), {
    slice: (from, to) => buffer.subarray(from, to),
    size: buffer.length,
  })

  if (decryptedChunks.isErr()) {
    return mapErr(
      decryptedChunks,
      '[decryptFileWithReportKey]: Could not decrypt buffer with report key'
    )
  }

  const totalLength = decryptedChunks.value.reduce((acc, chunk) => acc + chunk.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const chunk of decryptedChunks.value) {
    result.set(chunk, offset)
    offset += chunk.length
  }

  return ok(result)
}

// this function is only used on backend, `stream` is not available in browser
export const encryptBufferWithReportKey = async (fileBuffer: Buffer, reportKey: Uint8Array) => {
  const encryptedFile = await Stream.encrypt(Stream.toStreamKey(reportKey), {
    slice: (from, to) => fileBuffer.subarray(from, to),
    size: fileBuffer.byteLength,
  })

  if (encryptedFile.isErr()) {
    return mapErr(
      encryptedFile,
      '[encryptBufferWithReportKey]: Could not encrypt buffer with report key'
    )
  }

  try {
    // dynamically import stream to avoid bundling it in browser
    const { Readable } = await import('node:stream')
    return ok(Readable.from(Buffer.concat(encryptedFile.value)))
  } catch (e) {
    return createErr(
      '[encryptBufferWithReportKey]: Could not create readable stream from encrypted buffer',
      e
    )
  }
}

export const encryptFileWithReportKey = async (file: File, reportKey: Uint8Array) => {
  const buffer = await file.arrayBuffer()
  const encryptedFile = await Stream.encrypt(Stream.toStreamKey(reportKey), {
    slice: (from, to) => buffer.slice(from, to),
    size: file.size,
  })

  if (encryptedFile.isErr()) {
    return mapErr(
      encryptedFile,
      '[encryptFileWithReportKey]: Could not encrypt file with report key'
    )
  }

  return ok(
    // @ts-expect-error Ignore ArrayBuffer type
    new File(encryptedFile.value, file.name, {
      type: file.type,
    })
  )
}
